<script setup>
import AuthConsoleCard from '@/components/auth/AuthConsoleCard.vue';
import KHKNotificationCard from '@/components/ui/KHKNotificationCard.vue';
import { useSelectedBrand } from '@/composables/useSelectedBrand';
import { Button, Select } from 'primevue';
import { onMounted, ref } from 'vue';

// Post-login brand picker (#2122, #2230): a brand-neutral console screen reached
// after authentication and from the user popover. The admin chooses a brand and
// Continue persists it (PUT /admin/me/brand) then does a full load into the
// dashboard so every view re-fetches under the new brand. The options come
// straight from the backend (admin-selectable brands), never a hardcoded list.
const { selectedBrand, brands, loadingBrands, switching, loadBrands, switchBrand } = useSelectedBrand();

const chosenCode = ref(null);
const errorMessage = ref('');

onMounted(async () => {
    await loadBrands();
    // Default the dropdown to the brand currently in effect.
    chosenCode.value = selectedBrand.value?.code ?? null;
});

async function handleContinue() {
    errorMessage.value = '';
    try {
        // switchBrand persists (PUT /admin/me/brand) + full-reloads into '/', which
        // re-hydrates the whole app (including the store, header, and favicon)
        // under the newly selected brand.
        await switchBrand(chosenCode.value, '/');
    } catch (error) {
        console.error('Failed to switch brand', error);
        errorMessage.value = error?.message || 'Failed to switch brand. Please try again.';
    }
}
</script>

<template>
    <AuthConsoleCard>
        <KHKNotificationCard v-if="errorMessage" :notification="{ message: errorMessage }" type="error"
            class="w-full mt-4" />

        <div class="con-field">
            <!-- A span, not a <label for>: PrimeVue puts the id on the Select's root
                 div, which is not labelable, so only aria-labelledby names it. -->
            <span id="brand-select-label" class="con-label">Select brand</span>
            <Select v-model="chosenCode" aria-labelledby="brand-select-label" :options="brands" optionLabel="name"
                optionValue="code" :loading="loadingBrands || switching" :disabled="loadingBrands || switching"
                :placeholder="loadingBrands ? 'loading brands...' : 'choose a brand'" class="w-full auth-select"
                overlayClass="auth-select-panel" />
        </div>

        <Button @click="handleContinue" :loading="switching" :disabled="loadingBrands || switching || !chosenCode"
            class="auth-button w-full">
            <template #default>
                <div class="button-content">
                    <span>Continue</span>
                    <i class="bi bi-arrow-right"></i>
                </div>
            </template>
        </Button>

        <div class="con-foot">© {{ new Date().getFullYear() }} KHK Games. All rights reserved.</div>
    </AuthConsoleCard>
</template>
